import Actions from '../actions';
import { AppThunkAction } from '..';
import {
    AiScriberStatusChangedAction,
    AiScriberSessionStartedAction,
    AiScriberTranscriptDeltaReceivedAction,
    AiScriberElapsedTickAction,
    AiScriberResetAction,
    AiScriberReportDraftReceivedAction,
} from './types';
import { AiScriberStatus, AiScriberReportDraft } from './state';
import { startAiScriberSessionAsync } from '../../ajax/requests';
import { alertError } from '../alerts/actions';
import { translate, getLanguage } from '../translations/hooks';
import { TK } from '../translations/translationKeys';

const statusChanged = (
    status: AiScriberStatus,
    extras?: { appointmentId?: string; selectedTemplateId?: string; errorKey?: string }
): AiScriberStatusChangedAction => ({
    type: Actions.AiScriberStatusChanged,
    payload: { status, ...extras },
});

const sessionStarted = (sessionId: string): AiScriberSessionStartedAction => ({
    type: Actions.AiScriberSessionStarted,
    payload: { sessionId },
});

export const transcriptDeltaReceived = (delta: string): AiScriberTranscriptDeltaReceivedAction => ({
    type: Actions.AiScriberTranscriptDeltaReceived,
    payload: { delta },
});

export const aiScriberElapsedTick = (): AiScriberElapsedTickAction => ({
    type: Actions.AiScriberElapsedTick,
});

export const resetAiScriber = (): AiScriberResetAction => ({
    type: Actions.AiScriberReset,
});

export const openAiScriber = (appointmentId: string): AppThunkAction<void> =>
    (dispatch) => {
        dispatch(statusChanged('selectingReportTemplate', { appointmentId }));
    };

export const selectAiScriberTemplate = (
    appointmentId: string,
    templateId: string,
): AppThunkAction<Promise<void>> =>
    async (dispatch) => {
        dispatch(statusChanged('requestingMicrophonePermission', { appointmentId, selectedTemplateId: templateId }));

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach(t => t.stop()); // release immediately; component will re-acquire

            const { sessionId } = await startAiScriberSessionAsync(appointmentId, templateId, getLanguage());
            dispatch(sessionStarted(sessionId));
            dispatch(statusChanged('recording'));
        } catch (err: unknown) {
            const isPermissionError = err instanceof Error &&
                (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError');
            const errorKey = isPermissionError
                ? TK.aiScriberMicrophonePermissionDenied
                : TK.aiScriberStartError;
            dispatch(statusChanged('error', { errorKey }));
            if (!isPermissionError) {
                dispatch(alertError(translate(TK.aiScriberStartError)));
            }
        }
    };

export const aiScriberStopped = (): AppThunkAction<void> =>
    (dispatch) => {
        dispatch(statusChanged('stopping'));
    };

export const reportDraftReceived = (
    reportDraft: AiScriberReportDraft | null,
    templateId: string,
): AiScriberReportDraftReceivedAction => ({
    type: Actions.AiScriberReportDraftReceived,
    payload: { reportDraft, templateId },
});
